import { dataProtectionContent, landingContent, legalNoticeContent } from '../content';
import { site } from '../content/shared';
import type { Locale } from '../content/types';
import { getCanonicalPath, localizedPaths, type RouteKind } from './routes';

export type AlternateLink = {
  hrefLang: string;
  href: string;
};

export type SeoConfig = {
  title: string;
  description: string;
  canonical: string;
  alternates: AlternateLink[];
  ogTitle: string;
  ogDescription: string;
  ogType: 'website';
  ogLocale: string;
  image: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
};

const ogLocales: Record<Locale, string> = {
  en: 'en_US',
  de: 'de_CH',
};

export function absoluteUrl(path: string): string {
  return `${site.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

export function getAlternates(kind: RouteKind): AlternateLink[] {
  if (kind === 'not-found') {
    return [];
  }

  if (kind === 'legal-notice') {
    return [
      { hrefLang: 'en', href: absoluteUrl(localizedPaths.legalNotice.en) },
      { hrefLang: 'de-CH', href: absoluteUrl(localizedPaths.legalNotice.de) },
      { hrefLang: 'x-default', href: absoluteUrl(localizedPaths.legalNotice.en) },
    ];
  }

  if (kind === 'data-protection') {
    return [
      { hrefLang: 'en', href: absoluteUrl(localizedPaths.dataProtection.en) },
      { hrefLang: 'de-CH', href: absoluteUrl(localizedPaths.dataProtection.de) },
      { hrefLang: 'x-default', href: absoluteUrl(localizedPaths.dataProtection.en) },
    ];
  }

  return [
    { hrefLang: 'en', href: absoluteUrl(localizedPaths.landing.en) },
    { hrefLang: 'de-CH', href: absoluteUrl(localizedPaths.landing.de) },
    { hrefLang: 'x-default', href: absoluteUrl(localizedPaths.landing.en) },
  ];
}

export function getProfessionalServiceJsonLd(locale: Locale): Record<string, unknown> {
  const content = landingContent[locale];

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: site.name,
    url: `${site.baseUrl}/`,
    email: site.email,
    telephone: site.phoneSchema,
    address: {
      '@type': 'PostalAddress',
      streetAddress: site.addressStreet,
      postalCode: site.addressPostalCode,
      addressLocality: site.addressLocality,
      addressCountry: 'CH',
    },
    founder: {
      '@type': 'Person',
      name: site.name,
      jobTitle: 'Senior Software Engineer',
      url: `${site.baseUrl}/`,
      sameAs: [site.linkedin],
      knowsAbout: [
        'Software Architecture',
        'SaaS Development',
        'Cloud Computing',
        'DevOps',
        'AI Automation',
        'React',
        'Java',
        'Spring Boot',
        'Kubernetes',
        'Kafka',
      ],
    },
    areaServed: ['Switzerland', 'Zurich'],
    vatID: site.vat,
    taxID: site.uid,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: content.services.h2,
      itemListElement: content.services.items.map((service) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service.title,
          description: service.description,
          areaServed: 'Switzerland',
        },
      })),
    },
  };
}

export function getSeoConfig(kind: RouteKind, locale: Locale): SeoConfig {
  const canonicalPath = getCanonicalPath(kind, locale);
  const canonical = absoluteUrl(canonicalPath);
  const image = absoluteUrl('/og-image.png');

  if (kind === 'legal-notice') {
    const page = legalNoticeContent[locale];

    return {
      title: page.title,
      description: page.description,
      canonical,
      alternates: getAlternates(kind),
      ogTitle: page.title,
      ogDescription: page.description,
      ogType: 'website',
      ogLocale: ogLocales[locale],
      image,
    };
  }

  if (kind === 'data-protection') {
    const page = dataProtectionContent[locale];

    return {
      title: page.title,
      description: page.description,
      canonical,
      alternates: getAlternates(kind),
      ogTitle: page.title,
      ogDescription: page.description,
      ogType: 'website',
      ogLocale: ogLocales[locale],
      image,
    };
  }

  if (kind === 'not-found') {
    return {
      title: locale === 'de' ? 'Seite nicht gefunden | Maxim Domentii' : 'Page not found | Maxim Domentii',
      description:
        locale === 'de'
          ? 'Die angeforderte Seite wurde nicht gefunden.'
          : 'The requested page could not be found.',
      canonical,
      alternates: [],
      ogTitle: locale === 'de' ? 'Seite nicht gefunden' : 'Page not found',
      ogDescription:
        locale === 'de'
          ? 'Die angeforderte Seite wurde nicht gefunden.'
          : 'The requested page could not be found.',
      ogType: 'website',
      ogLocale: ogLocales[locale],
      image,
      noindex: true,
    };
  }

  const content = landingContent[locale];

  return {
    title: content.seo.title,
    description: content.seo.description,
    canonical,
    alternates: getAlternates('landing'),
    ogTitle: content.seo.ogTitle,
    ogDescription: content.seo.ogDescription,
    ogType: 'website',
    ogLocale: ogLocales[locale],
    image,
    jsonLd: getProfessionalServiceJsonLd(locale),
  };
}
